import React, {useEffect, useState} from 'react';
import {
  IonAccordion,
  IonAccordionGroup,
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonNote,
  IonSpinner,
  IonText,
} from '@ionic/react';
import {DateTime} from 'luxon';
import {post} from '../../utils/frRequest';
import {useAppDispatch, useAppSelector} from '../../lib/hooks';
import {actions} from '../../lib/app';
import {
  useDetalhesParcial, useExecucaoEmAndamentoPorSolicitacao, useExecucaoParcial,
  useSolicitacoesParciais, useUserTipo, useUsuariosParaSelecao,
} from '../../lib/DataHooks/transferencias';
import {TipoItemParcial, TransferenciaParcial, TransferenciaParcialSelecao} from '../../models/TransferenciaParcial';
import SelecionarUsuarioTransferencia from './SelecionarUsuarioTransferencia';
import SelecionarItensParciaisPage from './SelecionarItensParciaisPage';
import ConfirmationButton from '../../components/ConfirmationButton';

const TIPOS_SELECAO: {chave: TipoItemParcial; label: string}[] = [
  {chave: 'fazendas', label: 'fazenda(s)'},
  {chave: 'lojas', label: 'loja(s)'},
  {chave: 'veterinarios', label: 'veterinário(s)'},
];

const TransferenciaParcialPage: React.FC = () => {
  const {solicitacoes, isLoading} = useSolicitacoesParciais();

  return (
    <div className="p-4">
      <NovaSolicitacao/>

      <h2 className="text-lg font-semibold mt-6 mb-2">Solicitações pendentes</h2>
      {isLoading && (
        <div className="flex justify-center mt-4">
          <IonSpinner/>
        </div>
      )}
      {!isLoading && solicitacoes.length === 0 && (
        <IonText color="medium">Nenhuma solicitação parcial pendente.</IonText>
      )}
      {solicitacoes.map(s => (
        <SolicitacaoCard key={s.id} solicitacao={s}/>
      ))}
    </div>
  );
};

const NovaSolicitacao: React.FC = () => {
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [deUid, setDeUid] = useState('');
  const [paraUid, setParaUid] = useState('');
  const [selecao, setSelecao] = useState<TransferenciaParcialSelecao | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [erro, setErro] = useState('');
  const {usuarios, isLoading: isLoadingUsuarios} = useUsuariosParaSelecao();

  const deUsuario = usuarios.find(u => u.uid === deUid) || null;
  const paraUsuario = usuarios.find(u => u.uid === paraUid) || null;

  const validar = () => {
    if (!deUid || !paraUid) return 'Selecione os dois usuários.';
    if (deUid === paraUid) return 'Os usuários DE e PARA devem ser diferentes.';
    return '';
  };

  const handleAvancar = () => {
    const erroValidacao = validar();
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }
    setErro('');
    setStep(2);
  };

  const handleSelecaoAvancar = (novaSelecao: TransferenciaParcialSelecao) => {
    setSelecao(novaSelecao);
    setErro('');
    setStep(3);
  };

  const handleCriar = async () => {
    if (!selecao) return;

    setErro('');
    setIsLoading(true);

    const res = await post('/veApi/transferencias/criar_parcial', {
      de_uid: deUid,
      para_uid: paraUid,
      selecao,
    });

    setIsLoading(false);

    if (!res.success) {
      setErro(res.error || 'Ocorreu um erro ao criar a solicitação.');
      return;
    }

    dispatch(actions.notifySuccess('Solicitação de transferência parcial criada.'));
    setStep(4);
  };

  const handleNovaSolicitacao = () => {
    setDeUid('');
    setParaUid('');
    setSelecao(null);
    setErro('');
    setStep(1);
  };

  const totalSelecionado = selecao
    ? TIPOS_SELECAO.reduce(
      (total, {chave}) => total + selecao[chave].transferir.length + selecao[chave].desativar.length, 0)
    : 0;

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Nova solicitação de transferência parcial</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        {step === 1 && (
          <>
            <h2 className="text-lg font-semibold mb-4">Passo 1 — Selecionar usuários</h2>

            <SelecionarUsuarioTransferencia
              label="Usuário DE (origem)"
              usuarios={usuarios}
              isLoading={isLoadingUsuarios}
              value={deUid}
              onChange={setDeUid}
            />

            <div className="mt-2">
              <SelecionarUsuarioTransferencia
                label="Usuário PARA (destino)"
                usuarios={usuarios}
                isLoading={isLoadingUsuarios}
                value={paraUid}
                onChange={setParaUid}
              />
            </div>

            {erro && (
              <IonNote color="danger" className="block mt-2">
                {erro}
              </IonNote>
            )}

            <div className="mt-4">
              <IonButton
                expand="block"
                onClick={handleAvancar}
                disabled={isLoadingUsuarios || usuarios.length === 0}
              >
                Avançar
              </IonButton>
            </div>
          </>
        )}

        {step === 2 && deUsuario && (
          <>
            <h2 className="text-lg font-semibold mb-4">Passo 2 — Selecionar itens</h2>

            <SelecionarItensParciaisPage
              deUid={deUid}
              deNome={deUsuario.nome}
              onAvancar={handleSelecaoAvancar}
              onVoltar={() => setStep(1)}
            />
          </>
        )}

        {step === 3 && deUsuario && paraUsuario && selecao && (
          <>
            <h2 className="text-lg font-semibold mb-4">Passo 3 — Confirmar solicitação</h2>

            <IonText>
              <strong>{totalSelecionado}</strong> item(ns) de <strong>{deUsuario.nome}</strong> (DE) serão
              transferidos ou desativados para <strong>{paraUsuario.nome}</strong> (PARA). Nenhum dado é
              alterado até a aprovação final ser concedida.
            </IonText>

            {erro && (
              <IonNote color="danger" className="block mt-2">
                {erro}
              </IonNote>
            )}

            <div className="mt-4 flex gap-2">
              {isLoading
                ? <div className="flex justify-center w-full"><IonSpinner/></div>
                : (
                  <>
                    <IonButton fill="outline" onClick={() => setStep(2)} className="flex-1">
                      Voltar
                    </IonButton>
                    <IonButton onClick={handleCriar} className="flex-1">
                      Criar Solicitação
                    </IonButton>
                  </>
                )}
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <IonText color="success">Solicitação criada com sucesso.</IonText>
            <div className="mt-4">
              <IonButton expand="block" onClick={handleNovaSolicitacao}>
                Nova Solicitação
              </IonButton>
            </div>
          </>
        )}
      </IonCardContent>
    </IonCard>
  );
};

const statusLabel: Record<TransferenciaParcial['status'], string> = {
  aguardando_selecao: 'Aguardando seleção do destinatário',
  aguardando_aprovacao: 'Aguardando aprovação',
  aguardando_aprovacao_admin: 'Aguardando aprovação final do admin',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
};

const SolicitacaoCard: React.FC<{solicitacao: TransferenciaParcial}> = ({solicitacao}) => {
  const dispatch = useAppDispatch();
  const {isAdmin} = useUserTipo();
  const user = useAppSelector(state => state.app.user);
  const [isLoading, setIsLoading] = useState(false);
  const [detalhesAbertos, setDetalhesAbertos] = useState(false);
  const [execucaoId, setExecucaoId] = useState<string | null>(null);
  const {detalhes, isLoading: isLoadingDetalhes, erro: erroDetalhes, carregar: carregarDetalhes} = useDetalhesParcial();
  const execucao = useExecucaoParcial(execucaoId);
  const execucaoRecuperada = useExecucaoEmAndamentoPorSolicitacao(solicitacao.id);

  const data = solicitacao.criado_em?.toDate
    ? DateTime.fromJSDate(solicitacao.criado_em.toDate()).toFormat('dd/MM/yyyy HH:mm')
    : '';

  // recupera o estado "processando" se o card remontou depois de já ter uma execução em
  // andamento (ex.: usuário trocou de tela e voltou) — sem isso, o card mostraria os botões de
  // Aprovar/Cancelar de novo mesmo com uma aprovação já em curso
  useEffect(() => {
    if (execucaoRecuperada) {
      setExecucaoId(execucaoRecuperada);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execucaoRecuperada]);

  // a solicitação só vira 'concluida' (e some da listagem, via onSnapshot) depois que a execução
  // em background concluir com sucesso — em caso de erro ela permanece no status anterior, então
  // liberamos o card pra uma nova tentativa em vez de deixá-lo travado em "processando"
  useEffect(() => {
    if (execucao.status === 'concluida') {
      dispatch(actions.notifySuccess(
        solicitacao.criado_por_tipo === 'gestor' ? 'Aprovação registrada, aguardando decisão final do admin.' : 'Transferência aprovada.',
      ));
    } else if (execucao.status === 'erro') {
      dispatch(actions.notifyError(execucao.erro || 'Ocorreu um erro ao processar a transferência.'));
      setExecucaoId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execucao.status]);

  const processando = execucaoId !== null && execucao.status === 'em_andamento';

  const handleCancelar = async () => {
    setIsLoading(true);
    const res = await post('/veApi/transferencias/cancelar_parcial', {id: solicitacao.id});
    setIsLoading(false);

    if (!res.success) {
      dispatch(actions.notifyError(res.error || 'Ocorreu um erro ao cancelar a solicitação.'));
      return;
    }

    dispatch(actions.notifySuccess('Solicitação cancelada.'));
  };

  const handleAprovar = async () => {
    setIsLoading(true);
    const res = await post('/veApi/transferencias/aprovar_parcial', {id: solicitacao.id});
    setIsLoading(false);

    if (!res.success) {
      dispatch(actions.notifyError(res.error || 'Ocorreu um erro ao aprovar a solicitação.'));
      return;
    }

    const resData = res.data as {execucao_id?: string};
    if (resData.execucao_id) {
      setExecucaoId(resData.execucao_id);
    }
  };

  const handleAprovarFinal = async () => {
    setIsLoading(true);
    const res = await post('/veApi/transferencias/aprovar_parcial_admin', {id: solicitacao.id});
    setIsLoading(false);

    if (!res.success) {
      dispatch(actions.notifyError(res.error || 'Ocorreu um erro ao aprovar a solicitação.'));
      return;
    }

    const resData = res.data as {execucao_id?: string};
    if (resData.execucao_id) {
      setExecucaoId(resData.execucao_id);
    }
  };

  const selecao = solicitacao.selecao;
  const totalSelecionado = selecao
    ? TIPOS_SELECAO.reduce(
      (total, {chave}) => total + selecao[chave].transferir.length + selecao[chave].desativar.length, 0)
    : 0;

  const podeAprovarPrimeiraEtapa = solicitacao.status === 'aguardando_aprovacao'
    && (
      (solicitacao.criado_por_tipo === 'admin' && isAdmin)
      || (solicitacao.criado_por_tipo === 'gestor' && user.uid === solicitacao.criado_por_uid)
    );

  const podeAprovarEtapaFinal = solicitacao.status === 'aguardando_aprovacao_admin' && isAdmin;
  const podeVerDetalhes = podeAprovarPrimeiraEtapa || podeAprovarEtapaFinal;

  const handleToggleDetalhes = () => {
    if (!detalhesAbertos && !detalhes) {
      carregarDetalhes(solicitacao.id);
    }
    setDetalhesAbertos(atual => !atual);
  };

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardSubtitle>{data}</IonCardSubtitle>
        <IonCardTitle className="text-base">
          {solicitacao.de_nome} → {solicitacao.para_nome}
        </IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <IonBadge color={solicitacao.status.startsWith('aguardando_aprovacao') ? 'warning' : 'medium'}>
          {statusLabel[solicitacao.status]}
        </IonBadge>

        {(solicitacao.status === 'aguardando_aprovacao' || solicitacao.status === 'aguardando_aprovacao_admin')
          && selecao && (
            <div className="mt-2 text-sm">
              <p>
                {TIPOS_SELECAO.map(({chave, label}) => `${selecao[chave].transferir.length} ${label} para transferir`)
                  .join(', ')}.
              </p>
              <p>
                {TIPOS_SELECAO.map(({chave, label}) => `${selecao[chave].desativar.length} ${label} para desativar`)
                  .join(', ')}.
              </p>
            </div>
          )}

        {podeVerDetalhes && (
          <div className="mt-2">
            <IonButton fill="clear" size="small" onClick={handleToggleDetalhes}>
              {detalhesAbertos ? 'Ocultar detalhes' : 'Ver detalhes'}
            </IonButton>

            {detalhesAbertos && (
              <>
                {isLoadingDetalhes && (
                  <div className="flex justify-center py-2">
                    <IonSpinner/>
                  </div>
                )}

                {!isLoadingDetalhes && erroDetalhes && (
                  <IonNote color="danger" className="block">{erroDetalhes}</IonNote>
                )}

                {!isLoadingDetalhes && detalhes && (
                  <IonAccordionGroup multiple>
                    {TIPOS_SELECAO.map(({chave, label}) => {
                      const totalTipo = detalhes[chave].transferir.length + detalhes[chave].desativar.length;
                      return (
                        <IonAccordion key={chave} value={chave}>
                          <IonItem slot="header">
                            <IonLabel>
                              {label}
                              {totalTipo > 0 ? ` (${totalTipo})` : ''}
                            </IonLabel>
                          </IonItem>
                          <div slot="content">
                            <IonList>
                              <IonListHeader>
                                <IonLabel color="primary">
                                  <span className="text-xs font-semibold uppercase tracking-wide">
                                    Transferir ({detalhes[chave].transferir.length})
                                  </span>
                                </IonLabel>
                              </IonListHeader>
                              {detalhes[chave].transferir.length === 0 && (
                                <IonItem>
                                  <IonLabel color="medium">Nenhum item.</IonLabel>
                                </IonItem>
                              )}
                              {detalhes[chave].transferir.map(item => (
                                <IonItem key={item.id}>
                                  <IonLabel>
                                    <div className="flex items-center gap-1.5">
                                      <span
                                        className="mt-1.5 inline-block rounded-full bg-gray-500 shrink-0"
                                        style={{width: 8, height: 8}}
                                      />
                                      <div>
                                        <div className="font-medium">{item.nome}</div>
                                        {item.cidade && <div className="text-xs text-gray-500">{item.cidade}</div>}
                                      </div>
                                    </div>
                                  </IonLabel>
                                </IonItem>
                              ))}
                            </IonList>

                            <IonList>
                              <IonListHeader>
                                <IonLabel color="danger">
                                  <span className="text-xs font-semibold uppercase tracking-wide">
                                    Desativar ({detalhes[chave].desativar.length})
                                  </span>
                                </IonLabel>
                              </IonListHeader>
                              {detalhes[chave].desativar.length === 0 && (
                                <IonItem>
                                  <IonLabel color="medium">Nenhum item.</IonLabel>
                                </IonItem>
                              )}
                              {detalhes[chave].desativar.map(item => (
                                <IonItem key={item.id}>
                                  <IonLabel>
                                    <div className="flex items-center gap-1.5">
                                      <span
                                        className="mt-1.5 inline-block rounded-full bg-gray-500 shrink-0"
                                        style={{width: 8, height: 8}}
                                      />
                                      <div>
                                        <div className="font-medium">{item.nome}</div>
                                        {item.cidade && <div className="text-xs text-gray-500">{item.cidade}</div>}
                                      </div>
                                    </div>
                                  </IonLabel>
                                </IonItem>
                              ))}
                            </IonList>
                          </div>
                        </IonAccordion>
                      );
                    })}
                  </IonAccordionGroup>
                )}
              </>
            )}
          </div>
        )}

        {processando && (
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
            <IonSpinner/>
            <span>
              Processando em segundo plano...
              {execucao.demorando && ' Está demorando mais que o esperado.'}
            </span>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          {processando ? null : isLoading
            ? <div className="flex justify-center w-full"><IonSpinner/></div>
            : (
              <>
                {podeAprovarPrimeiraEtapa && (
                  <ConfirmationButton
                    color="success"
                    className="flex-1"
                    disabled={totalSelecionado === 0}
                    onConfirm={handleAprovar}
                    message="Tem certeza que deseja aprovar esta solicitação de transferência?"
                  >
                    Aprovar
                  </ConfirmationButton>
                )}
                {podeAprovarEtapaFinal && (
                  <ConfirmationButton
                    color="success"
                    className="flex-1"
                    onConfirm={handleAprovarFinal}
                    message="Tem certeza que deseja aprovar esta solicitação de transferência?"
                  >
                    Aprovar (final)
                  </ConfirmationButton>
                )}
                {isAdmin && (
                  <ConfirmationButton
                    color="danger"
                    fill="outline"
                    className="flex-1"
                    onConfirm={handleCancelar}
                    message="Tem certeza que deseja cancelar esta solicitação de transferência?"
                  >
                    Cancelar
                  </ConfirmationButton>
                )}
              </>
            )}
        </div>
      </IonCardContent>
    </IonCard>
  );
};

export default TransferenciaParcialPage;
